import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import api from '../../api';
import { 
  FiSearch, 
  FiUsers, 
  FiCheckCircle, 
  FiAlertCircle, 
  FiClock,
  FiChevronLeft,
  FiCalendar
} from 'react-icons/fi';

const TenantAcceptanceStatus = () => {
  const navigate = useNavigate();
  const { tenantId } = useParams();
  const [allTenants, setAllTenants] = useState([]);
  const [selectedTenant, setSelectedTenant] = useState(null);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [showTenantDetail, setShowTenantDetail] = useState(false);
  const [overallStats, setOverallStats] = useState({
    totalTenants: 0,
    tenantsWithPending: 0,
    tenantsWithDeadlinePassed: 0,
    tenantsWithAllAccepted: 0
  });

  const fetchAllTenants = useCallback(async () => {
    setLoading(true);
    try {
      const response = await api.get('/api/super-admin/tenants/acceptance-status');
      setAllTenants(response.data.data.tenants);
      setOverallStats(response.data.data.overallStats);
      
      // If tenantId is provided in URL, find and select that tenant
      if (tenantId) {
        const tenant = response.data.data.tenants.find(t => t.tenantId === tenantId);
        if (tenant) {
          setSelectedTenant(tenant);
          setShowTenantDetail(true);
        }
      }
    } catch (error) {
      console.error('Error fetching tenants:', error);
      alert('Failed to fetch tenants');
    } finally {
      setLoading(false);
    }
  }, [tenantId]);

  useEffect(() => {
    fetchAllTenants();
  }, [fetchAllTenants]);

  const handleTenantClick = (tenant) => {
    setSelectedTenant(tenant);
    setShowTenantDetail(true);
  };

  const handleBackToList = () => {
    setShowTenantDetail(false);
    setSelectedTenant(null);
    // Update URL to remove tenantId
    navigate('/super-admin/legal-documents/tenant-acceptance');
  };

  const formatDate = (date) => {
    if (!date) return 'N/A';
    return new Date(date).toLocaleDateString('en-IN', {
      day: '2-digit',
      month: 'short',
      year: 'numeric'
    });
  };

  const formatDateTime = (date) => {
    if (!date) return 'N/A';
    return new Date(date).toLocaleString('en-IN', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getDocumentTypeLabel = (type) => {
    const labels = {
      terms_of_service: 'Terms of Service',
      privacy_policy: 'Privacy Policy',
      refund_policy: 'Refund Policy',
      cookie_policy: 'Cookie Policy'
    };
    return labels[type] || type;
  };

  const getStatusBadge = (status) => {
    if (status === 'active') {
      return 'bg-emerald-50 text-emerald-700 border-emerald-200';
    } else if (status === 'suspended') {
      return 'bg-rose-50 text-rose-700 border-rose-200';
    }
    return 'bg-slate-100 text-slate-700 border-slate-200';
  };

  const filteredTenants = allTenants.filter(tenant =>
    tenant.tenantName.toLowerCase().includes(searchTerm.toLowerCase()) ||
    tenant.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    tenant.subdomain?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Loading state
  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-10 w-10 border-3 border-indigo-600 border-t-transparent"></div>
          <p className="mt-4 text-sm font-medium text-gray-500">Loading tenant acceptance data...</p>
        </div>
      </div>
    );
  }

  // Tenant Detail View
  if (showTenantDetail && selectedTenant) {
    return (
      <div className="min-h-screen bg-gray-50 text-gray-800 antialiased p-4 sm:p-6 lg:p-8">
        <div className="max-w-5xl mx-auto space-y-6">
          <div className="flex items-center gap-4">
            <button
              onClick={handleBackToList}
              className="text-xs font-semibold text-indigo-600 hover:text-indigo-700 transition-colors flex items-center gap-1 focus:outline-none"
            >
              <FiChevronLeft className="w-4 h-4" />
              Back to All Tenants
            </button>
          </div>

          {/* Tenant Header Card */}
          <div className="bg-white rounded-2xl shadow-sm border border-gray-200 overflow-hidden">
            <div className="bg-slate-900 px-6 py-6 text-white border-b border-slate-800 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
              <div>
                <span className="text-xs font-semibold text-indigo-300 uppercase tracking-wider block mb-1">
                  Tenant Details & Compliance
                </span>
                <h2 className="text-2xl font-bold tracking-tight text-white">{selectedTenant.tenantName}</h2>
                <div className="mt-2 flex flex-wrap items-center gap-3 text-xs text-slate-300 font-mono">
                  <span>ID: {selectedTenant.tenantId}</span>
                  <span>•</span>
                  <span>Email: {selectedTenant.email || 'N/A'}</span>
                  {selectedTenant.subdomain && (
                    <>
                      <span>•</span>
                      <span>Domain: {selectedTenant.subdomain}</span>
                    </>
                  )}
                </div>
              </div>

              <div className="flex sm:flex-col items-start sm:items-end justify-between gap-2">
                <span className={`px-3 py-1 text-xs font-semibold rounded-md border ${getStatusBadge(selectedTenant.status)}`}>
                  {selectedTenant.status?.toUpperCase() || 'UNKNOWN'}
                </span>
                <span className="text-xs text-slate-400">
                  Joined: {formatDate(selectedTenant.createdAt)}
                </span>
              </div>
            </div>

            {/* Summary Stats Grid */}
            <div className="p-6 bg-slate-50 border-b border-gray-100">
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
                <div className="bg-white rounded-xl p-4 border border-gray-200 text-center shadow-xs">
                  <p className="text-2xl font-extrabold text-gray-900">{selectedTenant.summary.total}</p>
                  <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider mt-1">Total Scope</p>
                </div>
                <div className="bg-white rounded-xl p-4 border border-emerald-100 text-center shadow-xs">
                  <p className="text-2xl font-extrabold text-emerald-600">{selectedTenant.summary.accepted}</p>
                  <p className="text-xs font-semibold text-emerald-700 uppercase tracking-wider mt-1">Accepted</p>
                </div>
                <div className="bg-white rounded-xl p-4 border border-amber-100 text-center shadow-xs">
                  <p className="text-2xl font-extrabold text-amber-600">{selectedTenant.summary.pending}</p>
                  <p className="text-xs font-semibold text-amber-700 uppercase tracking-wider mt-1">Pending</p>
                </div>
                {selectedTenant.summary.deadlinePassed > 0 ? (
                  <div className="bg-white rounded-xl p-4 border border-rose-200 text-center shadow-xs">
                    <p className="text-2xl font-extrabold text-rose-600">{selectedTenant.summary.deadlinePassed}</p>
                    <p className="text-xs font-semibold text-rose-700 uppercase tracking-wider mt-1">Deadline Passed</p>
                  </div>
                ) : (
                  <div className="bg-white rounded-xl p-4 border border-gray-200 text-center shadow-xs">
                    <p className="text-2xl font-extrabold text-gray-400">0</p>
                    <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider mt-1">Overdue</p>
                  </div>
                )}
              </div>
            </div>

            {/* Documents Detailed Status List */}
            <div className="p-6">
              <h3 className="text-xs font-bold text-gray-900 uppercase tracking-wider mb-4">
                Document Compliance Breakdown
              </h3>

              <div className="divide-y divide-gray-100 border border-gray-200 rounded-xl overflow-hidden bg-white">
                {selectedTenant.documents.map((doc) => (
                  <div key={doc.documentType} className="p-5 hover:bg-slate-50 transition-colors">
                    <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                      <div className="flex-1 space-y-2">
                        <div className="flex flex-wrap items-center gap-2">
                          <span className="font-bold text-gray-900 text-base">{getDocumentTypeLabel(doc.documentType)}</span>
                          <span className="px-2 py-0.5 text-xs font-mono font-semibold rounded bg-slate-100 text-slate-700 border border-slate-200">
                            v{doc.version}
                          </span>
                          
                          {doc.accepted ? (
                            <span className="px-2.5 py-0.5 text-xs font-medium rounded-md bg-emerald-50 text-emerald-700 border border-emerald-200 flex items-center gap-1">
                              <FiCheckCircle className="w-3.5 h-3.5" /> Accepted
                            </span>
                          ) : doc.isDeadlinePassed ? (
                            <span className="px-2.5 py-0.5 text-xs font-medium rounded-md bg-rose-50 text-rose-700 border border-rose-200 flex items-center gap-1">
                              <FiAlertCircle className="w-3.5 h-3.5" /> Deadline Passed
                            </span>
                          ) : (
                            <span className="px-2.5 py-0.5 text-xs font-medium rounded-md bg-amber-50 text-amber-700 border border-amber-200 flex items-center gap-1">
                              <FiClock className="w-3.5 h-3.5" /> Pending Signature
                            </span>
                          )}
                        </div>

                        <div className="flex flex-wrap gap-4 text-xs text-gray-500 font-medium">
                          <span className="flex items-center gap-1">
                            <FiCalendar className="w-3.5 h-3.5 text-slate-400" />
                            Effective: {formatDate(doc.effectiveDate)}
                          </span>

                          {doc.acceptanceEndDate && (
                            <span className={`flex items-center gap-1 ${doc.isDeadlinePassed ? 'text-rose-600 font-semibold' : 'text-amber-700'}`}>
                              <FiClock className="w-3.5 h-3.5" />
                              Deadline: {formatDate(doc.acceptanceEndDate)}
                              {doc.daysRemaining !== null && doc.daysRemaining > 0 && (
                                <span className="text-gray-400 font-normal">({doc.daysRemaining} days left)</span>
                              )}
                              {doc.daysRemaining !== null && doc.daysRemaining <= 0 && (
                                <span className="text-rose-600 font-semibold">(Passed)</span>
                              )}
                            </span>
                          )}

                          {doc.acceptedAt && (
                            <span className="text-emerald-700 font-semibold">
                              Signed: {formatDateTime(doc.acceptedAt)}
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Main List View
  return (
    <div className="min-h-screen bg-gray-50 text-gray-800 antialiased p-4 sm:p-6 lg:p-8">
      <div className="max-w-7xl mx-auto space-y-6">
        
        {/* Header Bar */}
        <div className="flex items-center justify-between pb-4 border-b border-gray-200">
          <div>
            <button
              onClick={() => navigate('/super-admin/legal-documents')}
              className="text-xs font-semibold text-indigo-600 hover:text-indigo-700 transition-colors flex items-center gap-1 mb-2 focus:outline-none"
            >
              <FiChevronLeft className="w-4 h-4" /> Back to Legal Documents
            </button>
            <h1 className="text-2xl sm:text-3xl font-extrabold tracking-tight text-gray-900">
              Tenant Acceptance Status
            </h1>
          </div>
        </div>

        {/* Aggregate Stats Cards */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
          <div className="bg-white rounded-xl p-5 border border-gray-200 shadow-xs">
            <p className="text-xs font-bold text-gray-500 uppercase tracking-wider">Total Tenants</p>
            <p className="text-3xl font-extrabold text-gray-900 mt-2">{overallStats.totalTenants}</p>
          </div>
          <div className="bg-white rounded-xl p-5 border border-emerald-200 shadow-xs">
            <p className="text-xs font-bold text-emerald-700 uppercase tracking-wider">All Accepted</p>
            <p className="text-3xl font-extrabold text-emerald-600 mt-2">{overallStats.tenantsWithAllAccepted}</p>
          </div>
          <div className="bg-white rounded-xl p-5 border border-amber-200 shadow-xs">
            <p className="text-xs font-bold text-amber-700 uppercase tracking-wider">Has Pending</p>
            <p className="text-3xl font-extrabold text-amber-600 mt-2">{overallStats.tenantsWithPending}</p>
          </div>
          <div className="bg-white rounded-xl p-5 border border-rose-200 shadow-xs">
            <p className="text-xs font-bold text-rose-700 uppercase tracking-wider">Deadline Passed</p>
            <p className="text-3xl font-extrabold text-rose-600 mt-2">{overallStats.tenantsWithDeadlinePassed}</p>
          </div>
        </div>

        {/* Search Bar */}
        <div className="bg-white rounded-xl shadow-xs p-4 border border-gray-200">
          <div className="relative">
            <div className="absolute inset-y-0 left-0 pl-3.5 flex items-center pointer-events-none">
              <FiSearch className="h-4 w-4 text-gray-400" />
            </div>
            <input
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Search tenants by name, email, or subdomain..."
              className="block w-full pl-10 pr-4 py-2.5 bg-gray-50 border border-gray-300 rounded-xl text-sm text-gray-900 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:bg-white transition-all"
            />
          </div>
        </div>

        {/* Tenants Table List */}
        {filteredTenants.length === 0 ? (
          <div className="bg-white rounded-2xl shadow-xs p-12 text-center border border-gray-200">
            <FiUsers className="w-12 h-12 text-gray-300 mx-auto mb-3" />
            <p className="text-sm font-semibold text-gray-600">No matching tenants found</p>
            <p className="text-xs text-gray-400 mt-1">Try refining your search filter</p>
          </div>
        ) : (
          <div className="bg-white rounded-2xl shadow-xs border border-gray-200 overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-left text-sm text-gray-600">
                <thead className="bg-slate-900 text-white text-xs uppercase tracking-wider">
                  <tr>
                    <th className="px-6 py-4 font-bold">Tenant Details</th>
                    <th className="px-6 py-4 font-bold">Status</th>
                    <th className="px-6 py-4 font-bold text-center">Accepted</th>
                    <th className="px-6 py-4 font-bold text-center">Pending</th>
                    <th className="px-6 py-4 font-bold text-center">Overdue</th>
                    <th className="px-6 py-4 font-bold text-right">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {filteredTenants.map((tenant) => (
                    <tr key={tenant.tenantId} className="hover:bg-slate-50 transition-colors">
                      <td className="px-6 py-4">
                        <div>
                          <p className="font-bold text-gray-900">{tenant.tenantName}</p>
                          <p className="text-xs text-gray-500">{tenant.email || 'No contact email'}</p>
                          {tenant.subdomain && (
                            <p className="text-xs font-mono text-indigo-600">{tenant.subdomain}</p>
                          )}
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <span className={`px-2.5 py-1 text-xs font-semibold rounded-md border ${getStatusBadge(tenant.status)}`}>
                          {tenant.status?.toUpperCase() || 'UNKNOWN'}
                        </span>
                      </td>
                      <td className="px-6 py-4 text-center">
                        <span className="text-emerald-600 font-bold font-mono">{tenant.summary.accepted}</span>
                      </td>
                      <td className="px-6 py-4 text-center">
                        <span className={`font-bold font-mono ${tenant.hasPending ? 'text-amber-600' : 'text-gray-400'}`}>
                          {tenant.summary.pending}
                        </span>
                      </td>
                      <td className="px-6 py-4 text-center">
                        <span className={`font-bold font-mono ${tenant.hasDeadlinePassed ? 'text-rose-600' : 'text-gray-400'}`}>
                          {tenant.summary.deadlinePassed}
                        </span>
                      </td>
                      <td className="px-6 py-4 text-right">
                        <button
                          onClick={() => handleTenantClick(tenant)}
                          className="px-3.5 py-2 bg-indigo-600 hover:bg-indigo-700 text-white rounded-lg font-medium text-xs transition-colors focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-1"
                        >
                          View Details
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default TenantAcceptanceStatus;